const BASE_URL = process.env.REACT_APP_API_URL || '/api';
const BACKEND_DOWN_MESSAGE =
  'Cannot connect to backend API. Start it with: npm --prefix server run dev';

const getToken = () => localStorage.getItem('pillpoint_admin_token');

const publicFetch = async (path, options = {}) => {
  let res;

  try {
    res = await fetch(`${BASE_URL}${path}`, {
      cache: 'no-store',
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        ...(options.headers || {}),
      },
    });
  } catch (error) {
    throw Object.assign(new Error(BACKEND_DOWN_MESSAGE), {
      cause: error,
      isNetworkError: true,
    });
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw Object.assign(new Error(err.message || 'API error'), { status: res.status });
  }

  return res.json();
};

const authFetch = async (path, options = {}) => {
  const token = getToken();
  let res;

  try {
    res = await fetch(`${BASE_URL}${path}`, {
      cache: 'no-store',
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });
  } catch (error) {
    throw Object.assign(new Error(BACKEND_DOWN_MESSAGE), {
      cause: error,
      isNetworkError: true,
    });
  }

  if (res.status === 401) {
    localStorage.removeItem('pillpoint_admin_token');
    window.location.href = '/meadmin/login';
    throw Object.assign(new Error('Session expired. Please sign in again.'), { status: 401 });
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw Object.assign(new Error(err.message || 'API error'), { status: res.status });
  }

  return res.json();
};

// Auth
export const adminLogin = (email, password) =>
  publicFetch('/admin/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

export const adminForgotPassword = ({ email, masterPassword, newPassword }) =>
  publicFetch('/admin/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email, masterPassword, newPassword }),
  });

export const adminGetMe = () => authFetch('/admin/auth/me');

// Dashboard
export const adminFetchDashboard = () => authFetch('/admin/dashboard');

// Products
export const adminFetchProducts = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return authFetch(`/admin/products${qs ? `?${qs}` : ''}`);
};

export const adminFetchCategories = () => publicFetch('/categories');

export const adminCreateProduct = (data) =>
  authFetch('/admin/products', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const adminUpdateProduct = (id, data) =>
  authFetch(`/admin/products/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

export const adminBulkUpdateProducts = (data) =>
  authFetch('/admin/products/bulk', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

export const adminDeleteProduct = (id) =>
  authFetch(`/admin/products/${id}`, { method: 'DELETE' });

// Orders
export const adminFetchOrders = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return authFetch(`/admin/orders${qs ? `?${qs}` : ''}`);
};

export const adminUpdateOrderStatus = (id, data) =>
  authFetch(`/admin/orders/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

export const adminUpdateOrder = (id, data) =>
  authFetch(`/admin/orders/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

// Blog
export const adminFetchBlog = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return authFetch(`/admin/blog${qs ? `?${qs}` : ''}`);
};

export const adminCreateBlogPost = (data) =>
  authFetch('/admin/blog', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const adminUpdateBlogPost = (id, data) =>
  authFetch(`/admin/blog/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

export const adminDeleteBlogPost = (id) =>
  authFetch(`/admin/blog/${id}`, { method: 'DELETE' });

// Admin Users
export const adminFetchUsers = () => authFetch('/admin/users');

export const adminCreateUser = (data) =>
  authFetch('/admin/users', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const adminResetUserPassword = (id, password, masterPassword) =>
  authFetch(`/admin/users/${id}/password`, {
    method: 'PATCH',
    body: JSON.stringify({ password, masterPassword }),
  });

export const adminDeleteUser = (id) =>
  authFetch(`/admin/users/${id}`, { method: 'DELETE' });

// Settings
export const adminFetchSettings = () => authFetch('/admin/settings');

export const adminUpdateSettings = (data) =>
  authFetch('/admin/settings', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

// Public settings (no auth)
export const fetchPublicSettings = () =>
  fetch(`${BASE_URL}/settings/public`).then((r) => r.json());
